/**
 * Pantalla completa en movil: quitar de en medio el navegador entero.
 *
 * Por que no bastaba con lo que ya habia:
 *
 * - El `<meta viewport viewport-fit=cover>` y el `100dvh` hacen que el juego
 *   ocupe el viewport REAL, pero el viewport real incluye la barra de
 *   direcciones. En un movil en horizontal esa barra se come entre un 12% y un
 *   18% del alto — que es justo el eje escaso, porque el campo se encaja por
 *   ancho — y ademas aparece y desaparece sola al deslizar, lo que reencuadra la
 *   camara en mitad de una jugada.
 * - `screen.orientation.lock()` FALLA fuera de pantalla completa en casi todos
 *   los navegadores. La reja de "gira el movil" existia precisamente porque el
 *   bloqueo no se podia pedir; dentro de pantalla completa si se puede, asi que
 *   entrar aqui es lo que hace que el bloqueo de horizontal empiece a funcionar
 *   de verdad.
 *
 * Lo que esto NO puede hacer, por mucho que se pida:
 *
 * - **Impedir cambiar de aplicacion.** Ninguna pagina web puede bloquear el
 *   gesto de inicio, el multitarea ni el boton de recientes; es del sistema
 *   operativo y esta asi a proposito. Lo mas cerca que se llega es esto: en
 *   Android, pantalla completa oculta tambien la barra de navegacion (modo
 *   inmersivo), asi que el back y el home dejan de estar a un roce del pulgar.
 *   El partido ya se pausa solo al perder el foco (`visibilitychange` en
 *   `main.ts`), que es la otra mitad del problema.
 * - **Pantalla completa en iPhone.** Safari de iOS no implementa
 *   `requestFullscreen` para elementos que no sean video. `supportsFullscreen()`
 *   devuelve false ahi y el boton ni se enseña; el camino en iPhone es "Añadir a
 *   pantalla de inicio", que arranca en modo standalone gracias al manifiesto y
 *   al `apple-mobile-web-app-capable` que ya estan puestos. En iPad si funciona.
 *
 * El zoom por pinza y el doble toque ya los cancela `touchErgonomics.ts`; eso no
 * necesita pantalla completa y sigue valiendo igual dentro y fuera.
 */

/** Preferencia del jugador, para no volver a pedirlo si lo ha rechazado. */
const STORAGE_KEY = "fynta.fullscreen";

type FullscreenDoc = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};

type FullscreenEl = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

function docEl(): FullscreenEl {
  return document.documentElement as FullscreenEl;
}

/** ¿El navegador implementa pantalla completa para elementos? */
export function supportsFullscreen(): boolean {
  if (typeof document === "undefined") return false;
  const el = docEl();
  return typeof el.requestFullscreen === "function" || typeof el.webkitRequestFullscreen === "function";
}

export function isFullscreen(): boolean {
  if (typeof document === "undefined") return false;
  const doc = document as FullscreenDoc;
  return Boolean(doc.fullscreenElement ?? doc.webkitFullscreenElement);
}

/**
 * ¿Se está ejecutando ya sin navegador alrededor?
 *
 * Es el caso de "Añadir a pantalla de inicio" en iOS y de la PWA instalada en
 * Android: ahi no hay barra que esconder, asi que ofrecer el boton seria
 * ofrecer un boton que no hace nada.
 */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia("(display-mode: standalone)").matches;
}

export function prefersFullscreen(): boolean {
  try {
    // Por defecto SI: en un movil, el juego a pantalla completa es lo que se
    // espera. Solo se recuerda el "no" explicito.
    return localStorage.getItem(STORAGE_KEY) !== "0";
  } catch {
    return true;
  }
}

function remember(on: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, on ? "1" : "0");
  } catch {
    // Sin almacenamiento se comporta como la primera vez. No es grave.
  }
}

/**
 * Mantiene la pantalla encendida mientras se juega.
 *
 * Un partido son cinco minutos en los que se toca la pantalla en dos sitios
 * fijos y muy poco: el movil lo interpreta como inactividad y se atenua justo
 * cuando el balon esta en juego. El bloqueo se libera solo al perder el foco, y
 * hay que volver a pedirlo al volver — de ahi el `visibilitychange`.
 */
let wakeLock: { release(): Promise<void>; released: boolean } | null = null;

async function requestWakeLock(): Promise<void> {
  const nav = navigator as Navigator & {
    wakeLock?: { request(type: "screen"): Promise<{ release(): Promise<void>; released: boolean }> };
  };
  if (!nav.wakeLock) return;
  try {
    wakeLock = await nav.wakeLock.request("screen");
  } catch {
    // Lo deniega si la pestaña no esta visible, y es correcto que lo deniegue.
  }
}

function releaseWakeLock(): void {
  void wakeLock?.release().catch(() => {
    /* ya liberado */
  });
  wakeLock = null;
}

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && isFullscreen()) void requestWakeLock();
    else releaseWakeLock();
  });
}

/**
 * Que Esc le llegue al JUEGO en pantalla completa (Chrome y Edge).
 *
 * Sin esto, el primer Esc lo usa el navegador para salir de pantalla completa
 * y el juego no se entera: el boton de Menu promete "Volver al menu (Esc)" y
 * en pantalla completa no volvia. Con el teclado bloqueado, un toque de Esc es
 * del juego y MANTENERLO sale de pantalla completa — el navegador lo avisa
 * solo, como en cualquier juego de navegador. Safari y Firefox no lo tienen:
 * alli sigue siendo un Esc para salir y otro para el menu.
 */
async function lockEscape(): Promise<void> {
  const keyboard = (navigator as Navigator & { keyboard?: { lock?: (keys?: string[]) => Promise<void> } })
    .keyboard;
  try {
    await keyboard?.lock?.(["Escape"]);
  } catch {
    // Sin permiso o sin la API: se queda como estaba.
  }
}

/**
 * Entra a pantalla completa. Tiene que llamarse DENTRO de un gesto del usuario
 * (o de la ventana de activacion que ese gesto deja abierta): los navegadores
 * rechazan la peticion en cualquier otro momento, y con razon.
 */
export async function enterFullscreen(lockLandscape = true): Promise<boolean> {
  if (!supportsFullscreen() || isFullscreen()) return isFullscreen();
  const el = docEl();
  try {
    // `navigationUI: "hide"` es lo que pide el modo inmersivo en Android: sin
    // el, la barra de navegacion del sistema se queda puesta.
    await (el.requestFullscreen?.({ navigationUI: "hide" }) ?? el.webkitRequestFullscreen?.());
  } catch {
    return false;
  }
  remember(true);
  void requestWakeLock();
  void lockEscape();
  if (lockLandscape) {
    // Ahora SI puede funcionar: el bloqueo de orientacion exige pantalla
    // completa en la mayoria de navegadores.
    const o = screen.orientation as ScreenOrientation & {
      lock?: (orientation: string) => Promise<void>;
    };
    try {
      await o?.lock?.("landscape");
    } catch {
      // Safari de iPad no lo permite ni asi. La reja de portrait sigue ahi.
    }
  }
  return isFullscreen();
}

export async function exitFullscreen(): Promise<void> {
  const doc = document as FullscreenDoc;
  if (!isFullscreen()) return;
  remember(false);
  releaseWakeLock();
  try {
    await (doc.exitFullscreen?.() ?? doc.webkitExitFullscreen?.());
  } catch {
    /* ya estaba fuera */
  }
}

export async function toggleFullscreen(): Promise<boolean> {
  if (isFullscreen()) {
    await exitFullscreen();
    return false;
  }
  return enterFullscreen();
}

/** Avisa de cada cambio (incluido salir con la tecla Escape del navegador). */
export function onFullscreenChange(fn: () => void): () => void {
  document.addEventListener("fullscreenchange", fn);
  document.addEventListener("webkitfullscreenchange", fn);
  return () => {
    document.removeEventListener("fullscreenchange", fn);
    document.removeEventListener("webkitfullscreenchange", fn);
  };
}
